import { Link } from "react-router-dom";
import { formatDate } from "@/lib/api";

function Meta({ article, dark }) {
  return (
    <div className={`flex items-center gap-2 text-xs font-sans ${dark ? "text-[#8FA2B3]" : "text-[#66717B]"}`}>
      <span>{article.author_name}</span>
      <span>&middot;</span>
      <span>{formatDate(article.published_at)}</span>
    </div>
  );
}

export function ArticleCard({ article, variant = "grid", dark = false }) {
  if (!article) return null;
  const testId = `article-card-${article.slug}`;

  if (variant === "hero") {
    return (
      <Link to={`/article/${article.slug}`} className="group block" data-testid={testId}>
        <div className="overflow-hidden bg-[#EDE7DD] mb-5 aspect-[16/10]">
          <img
            src={article.cover_image}
            alt={article.cover_image_alt || article.title}
            loading="eager"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#C47A35]" data-testid="article-card-category">
          {article.category_name || "Metri.bg"}
        </span>
        <h2 className="mt-2.5 font-serif text-3xl sm:text-4xl lg:text-[44px] font-bold text-[#10243A] leading-[1.12] group-hover:text-[#C47A35] transition-colors" data-testid="article-card-title">
          {article.title}
        </h2>
        <p className="mt-3 text-base sm:text-lg text-[#20252B]/80 leading-relaxed max-w-2xl">{article.subtitle}</p>
        <div className="mt-4">
          <Meta article={article} />
        </div>
      </Link>
    );
  }

  if (variant === "secondary") {
    return (
      <Link to={`/article/${article.slug}`} className="group flex gap-4 items-start" data-testid={testId}>
        <div className="overflow-hidden bg-[#EDE7DD] w-24 h-24 flex-shrink-0">
          <img src={article.cover_image} alt={article.cover_image_alt || article.title} className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#C47A35]" data-testid="article-card-category">
              {article.category_name || "Metri.bg"}
            </span>
            <span className="text-[10px] text-[#66717B]" data-testid="article-card-date">{formatDate(article.published_at)}</span>
          </div>
          <h3 className="mt-1 font-serif text-lg font-semibold text-[#10243A] leading-snug group-hover:text-[#C47A35] transition-colors" data-testid="article-card-title">
            {article.title}
          </h3>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link to={`/article/${article.slug}`} className={`group block ${dark ? "py-4" : ""}`} data-testid={testId}>
        {!dark && (
          <div className="overflow-hidden bg-[#EDE7DD] mb-3 aspect-[4/3]">
            <img src={article.cover_image} alt={article.cover_image_alt || article.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
          </div>
        )}
        <h3 className={`font-serif text-base font-semibold leading-snug transition-colors ${dark ? "text-white group-hover:text-[#C47A35]" : "text-[#10243A] group-hover:text-[#C47A35]"}`} data-testid="article-card-title">
          {article.title}
        </h3>
        {!dark && (
          <div className="mt-2">
            <Meta article={article} dark={dark} />
          </div>
        )}
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.slug}`} className="group block border-b border-[#E4E7E9] pb-6" data-testid={testId}>
      <div className="overflow-hidden bg-[#EDE7DD] mb-3 aspect-[16/10]">
        <img src={article.cover_image} alt={article.cover_image_alt || article.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
      </div>
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C47A35]" data-testid="article-card-category">
        {article.category_name || "Metri.bg"}
      </span>
      <h3 className="mt-2 font-serif text-xl font-bold text-[#10243A] leading-snug group-hover:text-[#C47A35] transition-colors" data-testid="article-card-title">
        {article.title}
      </h3>
      <p className="mt-2 text-sm text-[#66717B] line-clamp-2">{article.subtitle}</p>
      <div className="mt-3">
        <Meta article={article} />
      </div>
    </Link>
  );
}
